import React, {PropsWithChildren} from 'react';
import {STATUSES, Template} from '@chekin/core';
import {useChekinSDK} from '../ChekinSDK';
import {TailwindService} from 'services/TailwindService';
import {useFetchTemplate} from 'hooks/brandingTheme';
import {useCustomizeHeadContent} from 'hooks/useCustomizeHeadContent';
import {useTemplateCssVariables} from 'hooks/brandingTheme/useTemplateCssVariables';
import {useTemplateFonts} from 'hooks/brandingTheme/useTemplateFonts';

type ContextProps = {
  data?: Template;
  isTemplateInitialized: boolean;
  hasTemplate: boolean;
  isLoadedTemplate: boolean;
};

const BrandingThemeContext = React.createContext<ContextProps>({
  data: undefined,
  isTemplateInitialized: false,
  hasTemplate: true,
  isLoadedTemplate: false,
});

function BrandingThemeProvider({children}: Readonly<PropsWithChildren>) {
  const {templateId, isAuthInitialization} = useChekinSDK();

  const {fetchFonts, fontsStatus} = useTemplateFonts();

  const onTemplateSuccess = React.useCallback(
    (templateData: Template) => {
      void fetchFonts(templateData);
    },
    [fetchFonts],
  );

  const {data, isFetched, isError} = useFetchTemplate({
    templateId,
    onSuccess: onTemplateSuccess,
  });

  const hasTemplate = !isAuthInitialization && !!templateId;
  const isLoadedTemplate = !isAuthInitialization && (isFetched || isError);
  const isTemplateInitialized = hasTemplate ? isLoadedTemplate : !isAuthInitialization;

  const templateData = React.useMemo(() => data || ({} as Template), [data]);
  useCustomizeHeadContent(templateData?.favicon, templateData?.site_title);

  const tailwindVariables = useTemplateCssVariables(
    hasTemplate ? data : undefined,
    fontsStatus === STATUSES.success,
  );

  React.useEffect(() => {
    if (isTemplateInitialized && Object.keys(tailwindVariables).length > 0) {
      const tailwindService = TailwindService.getInstance();
      tailwindService.injectTailwindVariables(tailwindVariables);
    }

    return () => {
      if (isTemplateInitialized) {
        const tailwindService = TailwindService.getInstance();
        tailwindService.cleanup();
      }
    };
  }, [tailwindVariables, isTemplateInitialized]);

  const defaultProps = React.useMemo<ContextProps>(
    () => ({
      data: templateData,
      isTemplateInitialized,
      hasTemplate,
      isLoadedTemplate,
    }),
    [templateData, isTemplateInitialized, hasTemplate, isLoadedTemplate],
  );

  return <BrandingThemeContext.Provider value={defaultProps} children={children} />;
}

export {BrandingThemeContext, BrandingThemeProvider};
